/**
* @ngdoc    service
* @module   csm.remoting
* @name     csm.remoting.service:HTTPService
*
* @description
* Service for calling CSM RESTFul services which have a standardized response 
* pattern. Currently, this is a wrapper of methods that call `CSMHttp.callSvc()`.
* As the service response standards are updated, CSMHttp should be retired. 
**/
/* @ngInject */
export function httpService($injector) {
  var httpService = $injector.invoke(http);

  return {
    http: httpService,
    make: $injector.invoke(make, null, { httpService: httpService })
  };
}

httpService.$inject = ['$injector'];

/*******************************************************************************
* service.http
/**
* Direct service calls to `CSMHttp`. Successful requests return Promise resolved 
* with response `data` property. 
*******************************************************************************/
/* @ngInject */
function http(CSMHttp, Session, TemplateTextBuilder) {
  var http = {
    get: (route, data, transform, rejectAllFails) =>
      http
        .call('GET', route, data, transform, rejectAllFails)
        .then((r) => r.data),
    post: (route, data, transform, rejectAllFails) =>
      http
        .call('POST', route, data, transform, rejectAllFails)
        .then((r) => r.data),
    call: (method, route, data, transform, rejectAllFails) =>
      CSMHttp.callSvc(
        http.url(route),
        method,
        data,
        transform,
        rejectAllFails,
        true
      ),
    route: (route, parms) =>
      !!parms ? new TemplateTextBuilder(route, parms).toString() : route,
    url: (route) => Session.getSessionURL(route)
  };
  return http;
}

http.$inject = ['CSMHttp', 'Session', 'TemplateTextBuilder'];

/*******************************************************************************
* service.make
/**
* Returns HTTP service with methods for `base` controller and `services` routes.
* Successful requests return Promise resolved with response `data` property.
* Failed requests are checked for 418 (status) messages. All other codes are
* handled in `CSMHttp`.
*
* @param {string}          base      
* Base URL. Prepended to service routes.
* 
* @param {{object|function}|{object|function}[]} services
* Single or list of service methods. Each may be an injectable function returning 
* a service methods object where the key is the service route. If a list, final
* methods object is an aggregate. Functions get injected with `http` as a
* self-reference to allow for service methods defined as arrow (=>) functions 
* which have no `this` binding.
*******************************************************************************/
/* @ngInject */
function make($injector, $q, httpService) {
  var result = (http) =>
    $q((resolve, reject) =>
      http.then(
        (r) => resolve(r.data),
        (r) => {
          if (r.status == 418) {
            r.statusText = r.data.message;
            r.parsedResponse.message = () => r.statusText;
          }
          reject(r);
        }
      )
    );

  return function (base, services) {
    var path = (route) => (base || '') + route;
    var http = Object.create({
      get: (route, data, transform, rejectAllFails) =>
        result(
          httpService.call('GET', path(route), data, transform, rejectAllFails)
        ),
      post: (route, data, transform, rejectAllFails) =>
        result(
          httpService.call('POST', path(route), data, transform, rejectAllFails)
        ),
      route: (route, parms) => httpService.route(path(route), parms),
      url: (route) => httpService.url(path(route))
    });

    /*-------------------------------------------------------*/
    /* Service method object is an aggregate of the provided */
    /* list. If element is a function, inject with `http`.   */
    /*-------------------------------------------------------*/
    services = Array.isArray(services) ? services : [services];
    services = services.reduce(
      (r, e) =>
        Object.assign(
          r,
          typeof e == 'function'
            ? $injector.invoke(e, http, { http: http, path: path })
            : e || {}
        ),
      {}
    );

    return Object.assign(http, services);
  };
}

make.$inject = ['$injector', '$q', 'httpService'];
